import { Box, Flex, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useGetSourcingEventQuery, type BuyerBidDetailDto } from "../../../autogen/bff-api";
import { parseCriterionKey } from "../../../common/qualification/criterionKey";
import { getBidQualificationProgress, getBidQualificationRows } from "./bidQualification";
import { HiddenEntryDocumentationNotice } from "./HiddenEntryDocumentationNotice";
import { QualificationReviewCard } from "./QualificationReviewCard";
import { useBuyerQualificationReviewEditor } from "./useBuyerQualificationReviewEditor";
import { useStandingVerdictGuard } from "./useStandingVerdictGuard";

interface Props {
  eventId: string;
  bidId: string;
  detail: BuyerBidDetailDto;
}

export const QualificationsTab = ({ eventId, bidId, detail }: Props) => {
  const { t } = useTranslation();
  const editor = useBuyerQualificationReviewEditor(eventId, bidId, detail);
  const verdictGuard = useStandingVerdictGuard(detail);
  const { data: event } = useGetSourcingEventQuery({ eventId });
  const { hasRequirements, fulfilled, notFulfilled, pending, total } = getBidQualificationProgress(detail);
  const rows = getBidQualificationRows(detail);

  if (!hasRequirements) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="2"
        py="16"
        rounded="xl"
        bg="smBackground"
        border="1px dashed"
        borderColor="smBorder"
        color="smMuted"
      >
        <Text fontWeight="medium">{t("No qualification requirements")}</Text>
      </Flex>
    );
  }

  return (
    <Stack spacing="6">
      {event?.entriesAnonymous && <HiddenEntryDocumentationNotice />}
      <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" px="5" py="4">
        <Text color="smMuted" fontSize="sm">
          {pending === 0
            ? fulfilled === total
              ? t("All {{total}} requirements are fulfilled", { total })
              : t("{{count}} of {{total}} fulfilled", { count: fulfilled, total })
            : t("{{count}} of {{total}} reviewed", { count: fulfilled + notFulfilled, total })}
        </Text>
      </Box>

      {rows.map((row) => {
        const parsed = parseCriterionKey(row.criterionKey);
        return (
          <QualificationReviewCard
            key={row.criterionKey}
            type={parsed.type}
            name={parsed.name}
            documentationRequirement={row.criterion.description ?? null}
            supplierDescription={row.description ?? null}
            supplierEvidence={row.evidenceDocuments ?? []}
            verdict={editor.state.verdicts[row.criterionKey] ?? null}
            comment={editor.state.comments[row.criterionKey] ?? ""}
            onVerdictChange={(v) => verdictGuard.changeVerdict(v, () => editor.setVerdict(row.criterionKey, v))}
            onCommentChange={(v) => editor.setComment(row.criterionKey, v)}
          />
        );
      })}
      {verdictGuard.modal}
    </Stack>
  );
};
